import React, { useEffect, useState, useRef } from 'react';
import { LinksData } from '@/types';
import { ApiService } from '@/services/api';
import { useFacebookPixel } from '../context/FacebookPixelContext';
import './LandingPage.css';

declare global {
  interface Window {
    dataLayer: any[];
    fbq: any;
  }
}

const LandingPage: React.FC = () => {
  const [data, setData] = useState<LinksData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);
  const { trackEvent, isPixelLoaded } = useFacebookPixel();

  // Apply background color when data changes
  useEffect(() => {
    if (data && data.backgroundColor) {
      document.body.style.backgroundColor = data.backgroundColor;
    } else {
      document.body.style.backgroundColor = '#ffffff'; // Default white
    }
  }, [data]);

  useEffect(() => {
    let isCancelled = false;
    
    const fetchData = async () => {
      if (hasFetchedRef.current) {
        console.log('Skipping API call - already fetched');
        return;
      }
      
      hasFetchedRef.current = true;
      
      try {
        console.log('Starting API call...');
        setIsLoading(true);
        const apiService = ApiService.getInstance();
        const response = await apiService.getPublicData();



        console.log('API Response:', response);
        console.log('Response data structure:', response.data);
        
        // Ensure backgroundColor is present in the data
        if (response.data && response.success) {
          const dataWithBackgroundColor = {
            ...response.data,
            backgroundColor: response.data.backgroundColor || '#ffffff'
          };
          setData(dataWithBackgroundColor);
        } else {
          // Fallback data
          const fallbackData: LinksData = {
            title: 'Urubu do PIX',
            subtitle: 'Ganhe dinheiro fácil e rápido',
            whatsappTextBefore: 'Entrar no',
            whatsappTextAfter: 'WhatsApp',
            whatsappLink: 'https://wa.me/5511999999999',
            whatsappAvailable: true,
            telegramTextBefore: 'Entrar no',
            telegramTextAfter: 'Telegram',
            telegramLink: 'https://t.me/urubudopix',
            backgroundColor: '#ffffff'
          };
          setData(fallbackData);
        }

      } catch (err) {
        console.error('Fetch error:', err);
        
        // Use fallback data on network error
        const fallbackData: LinksData = {
          title: 'Urubu do PIX',
          subtitle: 'Ganhe dinheiro fácil e rápido',
          whatsappTextBefore: 'Entrar no',
          whatsappTextAfter: 'WhatsApp',
          whatsappLink: 'https://wa.me/5511999999999',
          whatsappAvailable: true,
          telegramTextBefore: 'Entrar no',
          telegramTextAfter: 'Telegram',
          telegramLink: 'https://t.me/urubudopix',
          backgroundColor: '#ffffff'
        };
        setData(fallbackData);

      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchData()
    
    // Cleanup function to cancel the request if component unmounts
  }, []); // Empty dependency array - only run once on mount

  // Helper function to check if Telegram data is valid and complete
  const isTelegramDataValid = (data: LinksData): boolean => {
    const { telegramTextBefore, telegramTextAfter, telegramLink } = data;
    
    return !!(
      telegramTextBefore && 
      telegramTextAfter && 
      telegramLink &&
      telegramTextBefore.trim() !== '' &&
      telegramTextAfter.trim() !== '' &&
      telegramLink.trim() !== ''
    );
  };

  const onWhatsAppClick = (data: LinksData) => {
    // Track click event with availability status
    window.dataLayer?.push({
      event: "WhatsAppClick",
      url: data.whatsappLink,
      whatsappAvailable: data.whatsappAvailable,
    });

    console.log("GTM WhatsApp event pushed:", {
      event: "WhatsAppClick",
      url: data.whatsappLink,
      whatsappAvailable: data.whatsappAvailable,
    });

    // Track Facebook pixel event using our dynamic system
    trackEvent("WhatsAppClick", {
      url: data.whatsappLink,
      whatsappAvailable: data.whatsappAvailable,
    });

    console.log("Facebook Pixel event tracked:", "WhatsAppClick", {
      isPixelLoaded,
      url: data.whatsappLink,
      whatsappAvailable: data.whatsappAvailable,
    });

    // Only open link if WhatsApp is available
    if (data.whatsappAvailable) {
      window.open(data.whatsappLink, "_blank");
    }
  };

  const onTelegramClick = (data: LinksData) => {
    // Track GTM event
    window.dataLayer?.push({
      event: "TelegramClick",
      url: data.telegramLink,
    });

    console.log("GTM Telegram event pushed:", {
      event: "TelegramClick",
      url: data.telegramLink,
    });

    // Track Facebook pixel event using our dynamic system
    trackEvent("TelegramClick", {
      url: data.telegramLink,
    });

    console.log("Facebook Pixel event tracked:", "TelegramClick", {
      isPixelLoaded,
      url: data.telegramLink,
    });

    // Open Telegram link
    window.open(data.telegramLink, "_blank");
  };

  if (isLoading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Carregando...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <p>Erro: {error}</p>
        <button onClick={() => window.location.reload()}>
          Tentar Novamente
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="error">
        <p>Nenhum dado encontrado.</p>
        <p>Debug: Data is {data === null ? 'null' : 'undefined'}</p>
      </div>
    );
  }

  console.log('Rendering with data:', data);

  return (
    <main>
      <div className="container">
        <section className="cabecalho">
          <div>
            <img className="logo" src="/assets/images/urubu-logo.svg" alt="Urubu Logo" />
          </div>
          <div>
            <h1>
              <span className="titulo">{data.title || 'Título não disponível'}</span>
            </h1>
            <p>
              <span className="subtitulo">{data.subtitle || 'Subtítulo não disponível'}</span>
            </p>
          </div>
        </section>

        <section className="links">
          <div>
            <div>
              <button 
                className={`whatsapp ${!data.whatsappAvailable ? 'disabled' : ''}`}
                onClick={() => onWhatsAppClick(data)}
              >
                <img
                  src="/assets/images/whatsapp logo.png"
                  alt="WhatsApp Logo"
                />
                <p className="whatsapp-text">
                  <span className="text-before">{data.whatsappTextBefore || 'WhatsApp'}</span>
                  <span className=" text-after">{data.whatsappTextAfter || 'Contato'}</span>
                </p>
              </button>
            </div>
            {/* Only show Telegram button if all required fields are properly filled */}
            {isTelegramDataValid(data) && (
              <button
                className="telegram"
                onClick={() => onTelegramClick(data)}
              >
                <img src="/assets/images/telegram logo.png" alt="Telegram Logo" />
                <p className="telegram-text">
                  <span className="text-before">{data.telegramTextBefore || 'Telegram'}</span>
                  <span className="text-after">{data.telegramTextAfter || 'Contato'}</span>
                </p>
              </button>
            )}

          </div>
        </section>
      </div>
    </main>
  );
};

export default LandingPage;